import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GlobalContext } from "../../Context/GlobalContext";
import StatsMarquee from "../Dashboard/orders/StatsMarquee";
import RevolvingBelt from "../Dashboard/orders/RevolvingBelt";
import OrderTable from "../Dashboard/orders/OrderTable";
import OrderDetailsModal from "../Dashboard/orders/OrderDetailsModal";

import {
  FileText,
  ClipboardList,
  ClipboardCheck,
  CalendarCheck,
  CreditCard,
  Truck,
  XCircle,
  Play,
  ChefHat,
  ShoppingCart,
  Package,
  UtensilsCrossed,
  SlidersHorizontal,
  Calendar,
  ArrowRight,
  MapPin,
} from "lucide-react";
import { toast } from "react-hot-toast";
import "../Dashboard/orders/orders.css";
import useDashboard from "../Services/DashboardServices/useDashboard";
import useClients from "../Services/ClientServices/useClient";

export default function OrdersDashboard({ openChatDrawer, refreshTick = 0 }) {
  const { getAllOrders, getOrderAnalytics, getClientOrderSummary } =
    useDashboard();
  const { getRestaurantLocation } = useClients();

  const { adminId, ordersUpdateTick } = useContext(GlobalContext);
  const navigate = useNavigate();

  // ===== Tabs =====
  const [tab, setTab] = useState("live"); // live | summary

  // ===== Live Orders state =====
  const [orders, setOrders] = useState([]);
  const [latestId, setLatestId] = useState(null);
  const [newOrderFlag, setNewOrderFlag] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  // ===== Summary state =====
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [summaryRows, setSummaryRows] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // ===== Location filter (Order Summary) =====
  // API: { pk_ml_id, location, status: "Y" }
  const [locationOptions, setLocationOptions] = useState([]); // [{ id, name }]
  const [locationId, setLocationId] = useState(""); // selected pk_ml_id or ""

  const [now, setNow] = useState(Date.now());

  // ===== Order details modal =====
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const orderLimit = 100;
  const [orderPage, setOrderPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const handleOpenOrderDetailsFromDashboard = (row) => {
    if (!row?.orderId) return;
    setSelectedOrderId(row.orderId);
    setDetailsModalOpen(true);
  };

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

// Load master locations once — default to first location
useEffect(() => {
  let cancelled = false;

  const loadLocations = async () => {
    if (typeof getRestaurantLocation !== "function") return;
    try {
      const res = await getRestaurantLocation({});
      if (cancelled) return;

      if (res?.responseCode === "00" && Array.isArray(res.response)) {
        const opts = res.response
          .filter((item) => String(item?.status || "").toUpperCase() === "Y")
          .map((item) => ({
            id: String(item?.pk_ml_id ?? ""),
            name: String(item?.location || "").trim(),
          }))
          .filter((o) => o.id && o.name);

        const map = new Map();
        opts.forEach((o) => map.set(o.id, o));
        const sorted = Array.from(map.values()).sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        setLocationOptions(sorted);

        // Default to first location (e.g. Delhi/NCR → "1")
        if (sorted.length > 0) {
          setLocationId((prev) => prev || sorted[0].id);
        }
      } else {
        setLocationOptions([]);
      }
    } catch (e) {
      if (!cancelled) {
        console.error("load locations error:", e);
        setLocationOptions([]);
      }
    }
  };

  loadLocations();
  return () => {
    cancelled = true;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  // Always resolve admin sid
  const getAdminSid = () => {
    if (adminId) return String(adminId);
    try {
      const candidates = [
        { store: sessionStorage, key: "userData" },
        { store: localStorage, key: "userData" },
        { store: localStorage, key: "admin" },
        { store: localStorage, key: "currentAdmin" },
      ];
      for (const { store, key } of candidates) {
        const raw = store.getItem(key);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          const meta = parsed.adminMeta || parsed.user || parsed;
          const id =
            meta?.adminId ?? meta?.pkid ?? meta?.id ?? meta?.pk_admin_id ?? "";
          if (id) return String(id);
        } catch (_) {}
      }
    } catch (_) {}
    return "";
  };

  // ===== Stats template =====
  const STAT_TEMPLATE = useMemo(
    () => [
      {
        title: "Total Orders",
        key: "totalOrders",
        icon: <FileText size={20} />,
        color: "#3B82F6",
      },
      {
        title: "Received Orders",
        key: "receivedOrders",
        icon: <ClipboardList size={20} />,
        color: "#6366F1",
      },
      {
        title: "Accepted Orders",
        key: "acceptedOrders",
        icon: <ClipboardCheck size={20} />,
        color: "#10B981",
      },
      {
        title: "Initiated Orders",
        key: "initiatedOrders",
        icon: <Play size={20} />,
        color: "#0EA5E9",
      },
      {
        title: "Ready for Takeaway",
        key: "readyForTakewayOrders",
        icon: <ChefHat size={20} />,
        color: "#FACC15",
      },
      {
        title: "Delivered Orders",
        key: "deliveredOrders",
        icon: <Truck size={20} />,
        color: "#FB923C",
      },
      {
        title: "Completed Orders",
        key: "completedOrders",
        icon: <CalendarCheck size={20} />,
        color: "#22C55E",
      },
      {
        title: "Paid Orders",
        key: "paidOrders",
        icon: <CreditCard size={20} />,
        color: "#8B5CF6",
      },
      {
        title: "Rejected Orders",
        key: "rejectedOrders",
        icon: <XCircle size={20} />,
        color: "#EF4444",
      },
      {
        title: "Total Amount",
        key: "totalOrdersAmount",
        icon: <FileText size={20} />,
        color: "#3B82F6",
        isAmount: true,
      },
      {
        title: "Received Amount",
        key: "receivedOrdersAmount",
        icon: <ClipboardList size={20} />,
        color: "#6366F1",
        isAmount: true,
      },
      {
        title: "Accepted Amount",
        key: "acceptedOrdersAmount",
        icon: <ClipboardCheck size={20} />,
        color: "#10B981",
        isAmount: true,
      },
      {
        title: "Initiated Amount",
        key: "initiatedOrdersAmount",
        icon: <Play size={20} />,
        color: "#0EA5E9",
        isAmount: true,
      },
      {
        title: "Takeaway Amount",
        key: "readyForTakewayOrdersAmount",
        icon: <ChefHat size={20} />,
        color: "#FACC15",
        isAmount: true,
      },
      {
        title: "Delivered Amount",
        key: "deliveredOrdersAmount",
        icon: <Truck size={20} />,
        color: "#FB923C",
        isAmount: true,
      },
      {
        title: "Completed Amount",
        key: "completedOrdersAmount",
        icon: <CalendarCheck size={20} />,
        color: "#22C55E",
        isAmount: true,
      },
      {
        title: "Paid Amount",
        key: "paidOrdersAmount",
        icon: <CreditCard size={20} />,
        color: "#8B5CF6",
        isAmount: true,
      },
      {
        title: "Rejected Amount",
        key: "rejectedOrdersAmount",
        icon: <XCircle size={20} />,
        color: "#EF4444",
        isAmount: true,
      },
    ],
    [],
  );

  const stats = useMemo(() => {
    return STAT_TEMPLATE.map((item) => ({
      title: item.title,
      value: analytics
        ? item.isAmount
          ? `₹ ${Number(analytics[item.key] || 0).toFixed(2)}`
          : String(analytics[item.key] ?? "0")
        : item.isAmount
          ? "₹ N/A"
          : "N/A",
      icon: item.icon,
      color: item.color,
    }));
  }, [STAT_TEMPLATE, analytics]);

  // ===== Mappers =====
  const mapOrders = (list = []) =>
    list.map((item) => ({
      customer: item.customer_name,
      restaurant: item.organization_name,
      orderId: item.orderid,
      orderType: item.orderType,
      status: item.status,
      paymentMode: item.payment_mode,
      paymentStatus: item.payment_status,
      orderDate: item.order_date,
      deliveryPartnerStatus: item.delivery_partner_status,
      amount: `₹ ${parseFloat(item.totalOrderPrice || 0).toFixed(2)}`,
      customerMobile: item.customer_mobile,
      restaurantNumber: item.restaurant_support_number,
    }));

  const fetchOrders = async () => {
    const sid = getAdminSid();
    if (!sid) return;

    try {
      setOrdersLoading(true);
      const offset = (orderPage - 1) * orderLimit;

      const result = await getAllOrders({
        adminId: sid,
        limit: orderLimit,
        offset,
      });

      if (!result?.success) {
        setOrders([]);
        setHasNextPage(false);
        toast.error(result?.message || "Failed to fetch orders");
        return;
      }

      const currentOrders = Array.isArray(result.orders) ? result.orders : [];
      const mapped = mapOrders(currentOrders);
      setHasNextPage(currentOrders.length >= orderLimit);

      setOrders((prev) => {
        const prevFirstId = prev[0]?.orderId;
        const nextFirstId = mapped[0]?.orderId;
        setLatestId(nextFirstId || null);

        if (nextFirstId && prevFirstId && nextFirstId !== prevFirstId) {
          setNewOrderFlag(true);
          setTimeout(() => setNewOrderFlag(false), 1800);
        }
        return mapped;
      });
    } catch (error) {
      console.error("fetchOrders error:", error);
      setOrders([]);
      setHasNextPage(false);
      toast.error("Failed to fetch orders");
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    const sid = getAdminSid();
    if (!sid) return;
    const result = await getOrderAnalytics(sid);
    if (result.success) {
      setAnalytics(result.analytics);
    }
  };

  const fetchSummary = async () => {
    const sid = getAdminSid();
    if (!sid) return;

    setSummaryLoading(true);
    try {
      const result = await getClientOrderSummary({
        adminId: sid,
        date,
        // only send when a location is selected
        ...(locationId ? { locationId: String(locationId) } : {}),
      });

      if (result.success) {
        setSummaryRows(result.rows || []);
      } else {
        setSummaryRows([]);
        toast.error(result.message || "Failed to load summary");
      }
    } catch (e) {
      console.error("fetchSummary error:", e);
      setSummaryRows([]);
      toast.error("Failed to load summary");
    } finally {
      setSummaryLoading(false);
    }
  };

  // ===== Enriched orders =====
  const enrichedOrders = useMemo(() => {
    const MAX_MIN = 5;
    const cutoff = now - MAX_MIN * 60 * 1000;

    return orders.map((o, idx) => {
      let ms = null;
      if (o.orderDate) {
        const parsed = Date.parse(o.orderDate);
        if (!Number.isNaN(parsed)) ms = parsed;
      }

      const isRecent = ms && ms >= cutoff;
      const showNewBadge = idx < 5 && isRecent;
      const ps = (o.paymentStatus || "").toLowerCase();
      const isPaid = ps.includes("paid") && !ps.includes("unpaid");
      const isPaidLate = isPaid && ms && ms < cutoff;

      return { ...o, showNewBadge, isPaidLate };
    });
  }, [orders, now]);

  // ===== Lifecycle =====
  useEffect(() => {
    const sid = getAdminSid();
    if (!sid) return;
    fetchOrders();
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminId, orderPage]);

  useEffect(() => {
    if (!ordersUpdateTick) return;
    fetchOrders();
    fetchAnalytics();
    if (tab === "summary") fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersUpdateTick, tab]);

useEffect(() => {
  if (tab === "summary" && locationId) {
    fetchSummary();
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [tab, locationId]);

  useEffect(() => {
    if (!refreshTick) return;
    fetchOrders();
    fetchAnalytics();
    if (tab === "summary") fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick]);

  // ===== Summary helpers =====
  const orderTypeSet = useMemo(() => {
    const s = new Set();
    summaryRows.forEach((r) =>
      (r.orderTypes || []).forEach((t) =>
        s.add(String(t.orderType || "").toLowerCase()),
      ),
    );
    return s;
  }, [summaryRows]);

  const orderedTypes = useMemo(() => {
    const preferred = ["office_order", "home_delivery", "takeaway", "dining"];
    const have = Array.from(orderTypeSet);
    return [
      ...preferred.filter((p) => have.includes(p)),
      ...have.filter((h) => !preferred.includes(h)),
    ];
  }, [orderTypeSet]);

  const totals = useMemo(() => {
    const t = {};
    orderedTypes.forEach((k) => (t[k] = 0));
    let grand = 0;
    summaryRows.forEach((r) => {
      (r.orderTypes || []).forEach((ti) => {
        const key = String(ti.orderType || "").toLowerCase();
        const cnt = Number(ti.orderCount || 0);
        if (key) t[key] = (t[key] || 0) + cnt;
        grand += cnt;
      });
    });
    return { byType: t, grand };
  }, [summaryRows, orderedTypes]);

  const fmt = (n) => new Intl.NumberFormat("en-IN").format(Number(n || 0));

  return (
    <div className="orders-root">
      {/* Top routing */}
      <div className="mb-3 rounded-xl border bg-white p-2">
        <div className="flex flex-wrap items-center gap-2">
          <RouteChip
            onClick={() => navigate("/admin/orders")}
            icon={<ShoppingCart size={16} />}
            label="Orders"
          />
          <RouteChip
            onClick={() => navigate("/admin/orders/settlement")}
            icon={<FileText size={16} />}
            label="Settlements"
          />
          <RouteChip
            onClick={() => navigate("/admin/orders/Catalogue")}
            icon={<Package size={16} />}
            label="Catalogue"
          />
          <RouteChip
            onClick={() => navigate("/admin/orders/Dinning")}
            icon={<UtensilsCrossed size={16} />}
            label="Dining"
          />
          <RouteChip
            onClick={() => navigate("/admin/orders/Pos")}
            icon={<CreditCard size={16} />}
            label="POS"
          />
          <RouteChip
            onClick={() => navigate("/admin/orders/PosOrders")}
            icon={<SlidersHorizontal size={16} />}
            label="Order Controller"
          />
        </div>
      </div>

      <StatsMarquee stats={stats} />

      {/* Tabs */}
      <div className="mt-3 rounded-xl border bg-white">
        <div className="flex items-center gap-2 border-b px-3 pt-2">
          <Tab
            active={tab === "live"}
            onClick={() => setTab("live")}
            label="Live Orders"
          />
          <Tab
            active={tab === "summary"}
            onClick={() => setTab("summary")}
            label="Order Summary"
          />
        </div>

        {tab === "live" && (
          <>
            <div className="p-2">
              <RevolvingBelt
                items={enrichedOrders.slice(0, 12)}
                labelKey="customer"
                subKey="amount"
                rightKey="status"
              />
            </div>

            <div className="w-full overflow-x-auto bg-white">
              <OrderTable
                orders={enrichedOrders}
                latestId={latestId}
                newOrderFlag={newOrderFlag}
                onRowClick={handleOpenOrderDetailsFromDashboard}
                openChatDrawer={openChatDrawer}
              />
            </div>

            <div className="flex items-center justify-end gap-2 border-t bg-white px-3 py-3">
              <button
                type="button"
                onClick={() => setOrderPage((prev) => Math.max(1, prev - 1))}
                disabled={orderPage === 1 || ordersLoading}
                className="h-9 rounded-lg border px-4 text-sm font-medium hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <span className="min-w-[40px] rounded-lg border bg-gray-50 px-3 py-2 text-center text-sm font-semibold">
                {orderPage}
              </span>
              <button
                type="button"
                onClick={() => setOrderPage((prev) => prev + 1)}
                disabled={!hasNextPage || ordersLoading}
                className="h-9 rounded-lg border px-4 text-sm font-medium hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </>
        )}

        {tab === "summary" && (
          <div className="p-3">
            {/* Filters: Location + Date */}
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
                  <ClipboardList size={16} />
                </div>
                <div>
                  <div className="text-sm font-semibold">
                    Client Order Summary
                  </div>
                  <div className="text-xs text-gray-500">
                    Per-client counts by order type (selected date)
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-end gap-2">
                <label className="text-sm">
                  <div className="mb-1 flex items-center gap-1 text-xs text-gray-500">
                    <MapPin size={14} /> Location
                  </div>
                  <select
                    className="h-9 min-w-[160px] rounded-xl border bg-white px-3 text-sm outline-none"
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                  >
                    
                    {locationOptions.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="text-sm">
                  <div className="mb-1 flex items-center gap-1 text-xs text-gray-500">
                    <Calendar size={14} /> Date
                  </div>
                  <input
                    type="date"
                    className="h-9 rounded-xl border px-3 text-sm outline-none"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </label>

                <button
                  type="button"
                  onClick={fetchSummary}
                  className="inline-flex h-9 items-center gap-2 rounded-xl border bg-white px-3 text-sm hover:bg-gray-50"
                >
                  <ArrowRight size={16} /> Apply
                </button>
              </div>
            </div>

            {/* Summary table */}
            <div className="max-h-[50vh] overflow-auto rounded-xl border">
              <table className="min-w-full text-left text-sm">
                <thead className="sticky top-0 bg-[#01C37D] text-white">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Client</th>
                    {orderedTypes.map((t) => (
                      <th key={t} className="px-3 py-2 font-semibold">
                        {labelize(t)}
                      </th>
                    ))}
                    <th className="px-3 py-2 font-semibold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryLoading && (
                    <tr>
                      <td
                        colSpan={orderedTypes.length + 2}
                        className="px-4 py-6 text-center text-gray-500"
                      >
                        Loading…
                      </td>
                    </tr>
                  )}
                  {!summaryLoading && summaryRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={orderedTypes.length + 2}
                        className="px-4 py-6 text-center text-gray-500"
                      >
                        No data.
                      </td>
                    </tr>
                  )}
                  {summaryRows.map((r, idx) => {
const counts = (r.orderTypes || []).reduce((acc, ot) => {
  const key = String(ot.orderType || "").toLowerCase();
  const count = Number(ot.orderCount || 0);

  if (key) {
    acc[key] = (acc[key] || 0) + count;
  }

  return acc;
}, {});

                    const rowTotal = Object.values(counts).reduce(
                      (a, b) => a + Number(b || 0),
                      0,
                    );
                    return (
                      <tr key={(r.clientName || "") + idx} className="border-t">
                        <td className="px-3 py-2">{r.clientName || "—"}</td>
                        {orderedTypes.map((t) => (
                          <td key={t} className="px-3 py-2 font-semibold">
                            {fmt(counts[t] || 0)}
                          </td>
                        ))}
                        <td className="px-3 py-2 font-semibold">
                          {fmt(rowTotal)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {!summaryLoading && summaryRows.length > 0 && (
                  <tfoot>
                    <tr className="border-t bg-gray-50 font-semibold">
                      <td className="px-3 py-2">Total</td>
                      {orderedTypes.map((t) => (
                        <td key={t} className="px-3 py-2">
                          {fmt(totals.byType[t] || 0)}
                        </td>
                      ))}
                      <td className="px-3 py-2">{fmt(totals.grand)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}
      </div>

      {detailsModalOpen && selectedOrderId && (
        <OrderDetailsModal
          orderId={selectedOrderId}
          open={detailsModalOpen}
          onClose={() => {
            setDetailsModalOpen(false);
            setSelectedOrderId(null);
          }}
        />
      )}
    </div>
  );
}

/* ---------- UI bits ---------- */

function RouteChip({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-2 rounded-lg border bg-white px-3 py-1.5 text-xs hover:bg-gray-50"
      title={label}
    >
      <span className="grid h-6 w-6 place-items-center rounded-md bg-[#01C37D]/10 text-[#01C37D]">
        {icon}
      </span>
      <span className="font-semibold text-gray-700">{label}</span>
    </button>
  );
}

function Tab({ active, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`relative px-3 py-2 text-sm font-semibold ${
        active ? "text-[#01C37D]" : "text-gray-500 hover:text-gray-700"
      }`}
    >
      {label}
      {active && (
        <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-[#01C37D]" />
      )}
    </button>
  );
}

/* ---------- Helpers ---------- */

function labelize(s) {
  const map = {
    office_order: "Office Order",
    home_delivery: "Home Delivery",
    takeaway: "Takeaway",
    dining: "Dine-In",
  };
  const key = String(s || "").toLowerCase();
  return (
    map[key] ||
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}